import React from 'react'
import { Box, Divider, ListItemButton, Theme, Tooltip, Typography, useTheme } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { primaryTint, sidebarSelectedTint } from '../../theme'
import { UserModel } from '../../models/UserModel'
import { UserAvatar } from './UserAvatar'

interface SidebarAccountFooterProps {
    labelsExpanded: boolean
    userModel: UserModel | null
    email: string
    selected: boolean
    onClick: () => void
    settingsLabel: string
    // Dev-only build/version info, folded into this row's own tooltip since a button can't
    // nest inside the button this row became; omitted entirely for everyone outside the team.
    versionLabel?: string
}

const getDisplayName = (userModel: UserModel | null, email: string): string => {
    if (userModel?.name_given && userModel?.name_family) {
        return `${userModel.name_given} ${userModel.name_family}`
    }
    return userModel?.name || email
}

const accountRowStyles = (labelsExpanded: boolean, theme: Theme) => ({
    m: 1,
    p: 1,
    borderRadius: '10px',
    minHeight: 40,
    gap: 1,
    justifyContent: labelsExpanded ? 'flex-start' : 'center',
    ...(!labelsExpanded && { width: 40, minWidth: 40, height: 40, mx: 'auto' }),
    '& .MuiSvgIcon-root': { color: 'text.disabled' },
    '&:hover': { backgroundColor: primaryTint(theme, 0.06) },
    '&:hover .MuiSvgIcon-root': { color: 'primary.main' },
    '&.Mui-focusVisible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
    },
    '&.Mui-selected': {
        backgroundColor: sidebarSelectedTint(theme),
        '& .MuiSvgIcon-root': { color: 'primary.main' },
        '&:hover': { backgroundColor: sidebarSelectedTint(theme) },
    },
})

export const SidebarAccountFooter: React.FC<SidebarAccountFooterProps> = ({
    labelsExpanded,
    userModel,
    email,
    selected,
    onClick,
    settingsLabel,
    versionLabel,
}) => {
    const theme = useTheme()
    // One tooltip, not a native title competing with it for the same hover: fold the version
    // in rather than let the browser's own tooltip fight MUI's for the same element.
    const tooltipTitle = versionLabel ? `${settingsLabel} (${versionLabel})` : settingsLabel

    return (
        <>
            {/* Hidden collapsed: a full-width line reads as a stray rule rather than a
                separator once the rail is just a column of icons. */}
            {labelsExpanded && <Divider />}
            {/* The whole row is one button (not an icon + separate label) so its hover/selected
                state reads as a single destination, same as the nav items above. */}
            <Tooltip title={tooltipTitle} placement="top">
                <ListItemButton
                    onClick={onClick}
                    selected={selected}
                    aria-label={settingsLabel}
                    sx={accountRowStyles(labelsExpanded, theme)}
                >
                    <UserAvatar userModel={userModel} email={email} />
                    {labelsExpanded && (
                        <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
                            <Typography noWrap sx={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.3 }}>
                                {getDisplayName(userModel, email)}
                            </Typography>
                            <Typography
                                noWrap
                                sx={{ fontSize: '10px', fontWeight: 400, color: 'text.secondary', lineHeight: 1.3 }}
                            >
                                {email}
                            </Typography>
                        </Box>
                    )}
                    {/* Outlined for the unselected state, filled once it's the active
                        destination — same convention the nav items above already follow. */}
                    {labelsExpanded &&
                        (selected ? (
                            <SettingsIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
                        ))}
                </ListItemButton>
            </Tooltip>
        </>
    )
}
